import { useCallback } from 'react';
import { useQuery } from '@tanstack/react-query';
import { useAuthOptional } from '@/context/AuthContext';
import type { FeatureFlagKey } from '@/config/featureFlags';

type ResolvedFlags = Record<string, boolean>;

/**
 * Fetches feature flag states resolved by the backend for the current user.
 * The backend evaluates base flags, rollout percentages, and scoped access
 * in one place — the frontend never computes rollout locally, avoiding
 * build-time vs runtime percentage drift.
 *
 * Usage:
 *   const { isEnabled, flags, isLoading } = useResolvedFeatureFlags();
 *   if (isEnabled('booking')) { ... }
 */
export function useResolvedFeatureFlags() {
  const auth = useAuthOptional();
  const isAuthenticated = auth?.isAuthenticated ?? false;
  const authLoading = auth?.isLoading ?? false;

  const { data, isLoading: flagsLoading, error, refetch } = useQuery<ResolvedFlags>({
    queryKey: ['/api/feature-flags/me'],
    queryFn: async () => {
      const response = await fetch('/api/feature-flags/me', {
        credentials: 'include',
      });
      if (!response.ok) {
        throw new Error('Failed to fetch resolved feature flags');
      }
      return response.json();
    },
    enabled: isAuthenticated,
    staleTime: 30 * 1000, // 30s — re-evaluates quickly when rollout % changes
    gcTime: 2 * 60 * 1000, // 2 minutes
    refetchOnWindowFocus: true,
    refetchOnMount: true,
    retry: 2,
  });

  const isEnabled = useCallback(
    (flag: FeatureFlagKey): boolean => {
      if (!isAuthenticated || !data) return false;
      return data[flag] ?? false;
    },
    [data, isAuthenticated],
  );

  return {
    flags: isAuthenticated ? (data ?? ({} as ResolvedFlags)) : ({} as ResolvedFlags),
    isEnabled,
    isLoading: authLoading || flagsLoading,
    error,
    refetch,
  };
}
